import { useCallback, useEffect, useRef, useState } from "react";

const GRID = 20; // coverage grid cell size (px)
const COVERAGE_BOOST = 1.3; // generous multiplier for kids with motor delays
const MIN_LINE_WIDTH = 4;
const PRESSURE_LINE_WIDTH = 18;

const readPressure = (e) => {
  if (e.pressure && e.pressure !== 0.5) return e.pressure;
  return e.pointerType === "pen" ? 0.5 : 0.6;
};

/**
 * useTracingCanvas
 *
 * Encapsulates a pressure-aware drawing canvas plus coverage scoring.
 *
 * Bind `setCanvasRef` (callback ref) to the <canvas> — it initializes the
 * canvas (DPR scaling, line caps) the instant it attaches to the DOM.
 *
 * Pass `resetKey` (e.g., the exercise id) to clear the canvas on navigation.
 */
export default function useTracingCanvas({ strokeColor = "#FF7A00", resetKey } = {}) {
  const canvasRef = useRef(null);
  const guideRef = useRef(null);
  const lastPos = useRef(null);
  const drawnCells = useRef(new Set());
  const pressureSamples = useRef([]);
  const startTime = useRef(null);

  const [drawing, setDrawing] = useState(false);
  const [coverage, setCoverage] = useState(0);
  const [hasDrawn, setHasDrawn] = useState(false);

  // Callback ref: initializes the canvas the moment it attaches to the DOM.
  // Avoids the useEffect-after-mount race where the canvas isn't yet rendered.
  const setCanvasRef = useCallback((node) => {
    canvasRef.current = node;
    if (!node) return;
    const rect = node.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;
    const dpr = window.devicePixelRatio || 1;
    node.width = Math.floor(rect.width * dpr);
    node.height = Math.floor(rect.height * dpr);
    const ctx = node.getContext("2d");
    ctx.setTransform(1, 0, 0, 1, 0, 0); // reset any prior scale
    ctx.scale(dpr, dpr);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
  }, []);

  // Reset drawing state when the exercise changes (same canvas, fresh slate).
  useEffect(() => {
    const c = canvasRef.current;
    if (c) {
      const ctx = c.getContext("2d");
      ctx.clearRect(0, 0, c.width, c.height);
    }
    drawnCells.current.clear();
    pressureSamples.current = [];
    startTime.current = null;
    setCoverage(0);
    setHasDrawn(false);
  }, [resetKey]);

  const getPos = useCallback((e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  }, []);

  const recomputeCoverage = useCallback(() => {
    if (!guideRef.current || !canvasRef.current) return;
    const c = canvasRef.current.getBoundingClientRect();
    const g = guideRef.current.getBoundingClientRect();
    const cellsW = Math.ceil(g.width / GRID);
    const cellsH = Math.ceil(g.height / GRID);
    const ox = Math.floor((g.left - c.left) / GRID);
    const oy = Math.floor((g.top - c.top) / GRID);
    let hits = 0;
    let total = 0;
    for (let x = ox; x < ox + cellsW; x++) {
      for (let y = oy; y < oy + cellsH; y++) {
        total++;
        if (drawnCells.current.has(`${x}:${y}`)) hits++;
      }
    }
    const cov = total > 0 ? Math.min(100, Math.round((hits / total) * 100 * COVERAGE_BOOST)) : 0;
    setCoverage(cov);
  }, []);

  const onPointerDown = useCallback((e) => {
    e.preventDefault();
    canvasRef.current.setPointerCapture(e.pointerId);
    if (startTime.current === null) startTime.current = Date.now();
    setDrawing(true);
    lastPos.current = getPos(e);
    pressureSamples.current.push(readPressure(e));
  }, [getPos]);

  const onPointerMove = useCallback((e) => {
    if (!drawing) return;
    const pos = getPos(e);
    const ctx = canvasRef.current.getContext("2d");
    const pressure = readPressure(e);
    ctx.strokeStyle = strokeColor;
    ctx.lineWidth = MIN_LINE_WIDTH + pressure * PRESSURE_LINE_WIDTH;
    ctx.beginPath();
    ctx.moveTo(lastPos.current.x, lastPos.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    lastPos.current = pos;
    pressureSamples.current.push(pressure);
    drawnCells.current.add(`${Math.floor(pos.x / GRID)}:${Math.floor(pos.y / GRID)}`);
    if (!hasDrawn) setHasDrawn(true);
    recomputeCoverage();
  }, [drawing, getPos, strokeColor, hasDrawn, recomputeCoverage]);

  const onPointerUp = useCallback(() => {
    setDrawing(false);
    recomputeCoverage();
  }, [recomputeCoverage]);

  const clear = useCallback(() => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    ctx.clearRect(0, 0, c.width, c.height);
    drawnCells.current.clear();
    pressureSamples.current = [];
    startTime.current = null;
    setCoverage(0);
    setHasDrawn(false);
  }, []);

  const duration = useCallback(() => {
    if (startTime.current === null) return 0;
    return Math.round((Date.now() - startTime.current) / 1000);
  }, []);

  const averagePressure = useCallback(() => {
    const samples = pressureSamples.current;
    if (samples.length === 0) return 0.5;
    return samples.reduce((a, b) => a + b, 0) / samples.length;
  }, []);

  const canvasHandlers = {
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onPointerLeave: onPointerUp,
  };

  return {
    setCanvasRef,
    canvasRef,
    guideRef,
    canvasHandlers,
    coverage,
    hasDrawn,
    drawing,
    clear,
    duration,
    averagePressure,
  };
}
